import { useState } from 'react';

import { Button as MuiButton, Stack, TextField } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import FactList, {
  type FactGroup,
} from '@/karehero-app/tell-us-once/intake/FactList';
import { type ReviewRow } from '@/karehero-app/tell-us-once/intake/content';

// What a card from us holds, opened from its pencil as answer rows in a drawer.
//
// One drawer, two pages. Tapping a row turns the drawer into that answer's
// editor, with Back to the list, rather than opening a second drawer over this
// one: the rows stay full-size touch targets and the edit still happens in a
// drawer. Done saves and goes back to the list.
const FactsSheet = ({
  title,
  groups,
  onClose,
  initialEditing,
}: {
  title: string;
  groups: FactGroup[];
  onClose: () => void;
  /** Harness-only: open on this row's editor. */
  initialEditing?: string;
}) => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<ReviewRow | null>(
    () =>
      groups
        .flatMap((g) => g.rows)
        .find((r) => r.id === initialEditing) ?? null,
  );
  const [draft, setDraft] = useState(editing?.value ?? '');

  const shown = groups.map((g) => ({
    ...g,
    rows: g.rows.map((r) => ({ ...r, value: edits[r.id] ?? r.value })),
  }));

  const open = (row: ReviewRow) => {
    setEditing(row);
    setDraft(edits[row.id] ?? row.value);
  };

  return (
    <BottomSheet
      title={editing ? editing.label : title}
      variant='hug'
      onClose={onClose}
      footer={
        editing ? (
          <MuiButton
            variant='contained'
            size='large'
            fullWidth
            onClick={() => {
              setEdits((prev) => ({ ...prev, [editing.id]: draft }));
              setEditing(null);
            }}
          >
            Done
          </MuiButton>
        ) : undefined
      }
    >
      {editing ? (
        <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
          <MuiButton
            variant='text'
            size='small'
            startIcon={<Icon icon={'chevron-left' as never} fontSize={12} />}
            onClick={() => setEditing(null)}
          >
            Back
          </MuiButton>
          <TextField
            fullWidth
            size='small'
            label={editing.label}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </Stack>
      ) : (
        <FactList groups={shown} edit='drawer' onOpenRow={open} />
      )}
    </BottomSheet>
  );
};

export default FactsSheet;
